
import { useRef } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { UploadCloud, XCircle } from "lucide-react";
import type { ArticleFormValues } from "./ArticleEditor";
import { Control } from "react-hook-form";

interface Props {
  control: Control<ArticleFormValues>;
  imagePreview: string | null;
  selectedFileName: string | null;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeSelectedImage: () => void;
}

export default function ArticleEditorImage({
  control,
  imagePreview,
  selectedFileName,
  handleImageChange,
  removeSelectedImage,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <FormField
      control={control}
      name="image_file"
      render={({ field: { value, onChange, ...fieldProps } }) => (
        <FormItem>
          <FormLabel className="text-white/90">Featured Image (Recommended size: 1200x630)</FormLabel>
          <FormControl>
            <div className="space-y-4">
              <div className="relative">
                <Input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    handleImageChange(e);
                    onChange(e.target.files);
                  }}
                  {...fieldProps}
                  className="bg-black/20 border-white/10 hidden"
                  ref={fileInputRef}
                />
                <div className="flex flex-col space-y-4">
                  <div className="border-2 border-dashed border-white/30 rounded-md p-4 text-center hover:border-white/50 transition-colors bg-black/20">
                    <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center">
                      <UploadCloud className="h-10 w-10 text-white/70 mb-2" />
                      <span className="text-white/70">Click to upload image</span>
                      <span className="text-white/50 text-sm">
                        {selectedFileName || "No file selected"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              {imagePreview && (
                <div className="w-full max-w-md border border-white/20 rounded-md overflow-hidden relative bg-black/20">
                  <AspectRatio ratio={16/9}>
                    <img
                      src={imagePreview}
                      alt="Article preview"
                      className="w-full h-full object-cover"
                    />
                  </AspectRatio>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 rounded-full w-8 h-8"
                    onClick={removeSelectedImage}
                  >
                    <XCircle className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
